import { motion } from 'framer-motion';

const timelineData = [
  {
    year: '1958',
    title: 'Perceptron',
    description: 'Rosenblatt creates the first artificial neuron, sparking the field of neural networks.',
    color: '#9B59B6',
  },
  {
    year: '1986',
    title: 'Backpropagation',
    description: 'Rumelhart, Hinton, and Williams popularize backpropagation, enabling deep learning.',
    color: '#9B59B6',
  },
  {
    year: '1989',
    title: 'CNNs',
    description: 'LeCun introduces LeNet, pioneering convolutional neural networks for image recognition.',
    color: '#3498DB',
  },
  {
    year: '1997',
    title: 'LSTMs',
    description: 'Hochreiter and Schmidhuber solve the vanishing gradient problem with LSTM networks.',
    color: '#3498DB',
  },
  {
    year: '2012',
    title: 'Deep Learning Renaissance',
    description: 'AlexNet wins ImageNet, proving deep learning works at scale. The AI revolution begins.',
    color: '#3498DB',
  },
  {
    year: '2014',
    title: 'GANs',
    description: 'Goodfellow introduces Generative Adversarial Networks, revolutionizing AI generation.',
    color: '#3498DB',
  },
  {
    year: '2017',
    title: 'Transformers',
    description: '"Attention Is All You Need" introduces the Transformer architecture, changing everything.',
    color: '#3498DB',
  },
  {
    year: '2020',
    title: 'Diffusion Models',
    description: 'DDPM paper launches the diffusion model era for high-quality image generation.',
    color: '#3498DB',
  },
  {
    year: '2022',
    title: 'ChatGPT Moment',
    description: 'AI goes mainstream. RLHF and instruction-following create conversational AI.',
    color: '#27AE60',
  },
  {
    year: '2023',
    title: 'Multimodal & Agents',
    description: 'GPT-4V, Gemini bring multimodal understanding. Agents and tool use emerge.',
    color: '#1ABC9C',
  },
  {
    year: '2024+',
    title: 'Reasoning Models',
    description: 'Thinking models, MoE architectures, and the path toward more capable AI.',
    color: '#FFD700',
  },
];

export default function Timeline() {
  return (
    <div className="max-w-6xl mx-auto px-4">
      {/* Section Header */}
      <motion.div
        className="text-center mb-12"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">
          The Evolution of <span className="text-gradient">AI Architectures</span>
        </h2>
        <p className="text-gray-400 max-w-2xl mx-auto">
          From the first artificial neuron to modern thinking models — 
          a journey through 70+ years of innovation.
        </p>
      </motion.div>

      {/* Architecture Evolution Image */}
      <motion.div
        className="mb-16"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <img
          src="/images/Architecture_Evolution_Timeline.png"
          alt="Evolution of Neural Network Architectures"
          className="w-full rounded-xl border border-white/10"
        />
      </motion.div>

      {/* Interactive Timeline */}
      <div className="relative">
        {/* Vertical Line */}
        <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-cyber-blue via-cyber-purple to-future" />

        {/* Timeline Items */}
        <div className="space-y-12">
          {timelineData.map((item, index) => (
            <motion.div
              key={item.year}
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: 0.1 }}
              className={`relative flex items-center ${
                index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
              } flex-row`}
            >
              {/* Content */}
              <div className={`ml-12 md:ml-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                <motion.div
                  className="p-6 rounded-xl bg-dark-800/50 border border-white/5 hover:border-white/10 transition-colors"
                  whileHover={{ scale: 1.02 }}
                >
                  <span
                    className="inline-block text-sm font-mono px-3 py-1 rounded-full mb-3"
                    style={{ backgroundColor: `${item.color}20`, color: item.color }}
                  >
                    {item.year}
                  </span>
                  <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                  <p className="text-gray-400 text-sm">{item.description}</p>
                </motion.div>
              </div>

              {/* Dot */}
              <div 
                className="absolute left-4 md:left-1/2 w-4 h-4 rounded-full -translate-x-1/2 border-4 border-dark-950 z-10"
                style={{ backgroundColor: item.color }}
              />

              {/* Spacer for alternating layout */}
              <div className="hidden md:block md:w-1/2" />
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
